import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Calendar, AlertCircle, User, Filter } from 'lucide-react';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegularizationApproval() {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState('Pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    remarks: '',
    approved_status: 'Present',
    approved_check_in: '',
    approved_check_out: ''
  });

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = filter === 'All' 
        ? `${API_URL}/api/regularization/all`
        : `${API_URL}/api/regularization/all?status=${filter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
        setStats(data.stats || { pending: 0, approved: 0, rejected: 0 });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/regularization/${selectedRequest.id}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Request approved and attendance updated!');
        setSelectedRequest(null);
        resetForm();
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve');
      }
    } catch (error) {
      toast.error('Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    if (!formData.remarks.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/regularization/${selectedRequest.id}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ remarks: formData.remarks })
      });

      if (response.ok) {
        toast.success('Request rejected');
        setSelectedRequest(null);
        resetForm();
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reject');
      }
    } catch (error) {
      toast.error('Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      remarks: '',
      approved_status: 'Present',
      approved_check_in: '',
      approved_check_out: ''
    });
  };

  const openRequest = (request) => {
    setSelectedRequest(request);
    setFormData({
      remarks: '',
      approved_status: request.requested_status || 'Present',
      approved_check_in: request.requested_check_in || '',
      approved_check_out: request.requested_check_out || ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'bg-green-100 text-green-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getRequestTypeIcon = (type) => {
    switch (type) {
      case 'Missed Check-In': return '🔓';
      case 'Missed Check-Out': return '🔒';
      case 'Wrong Time': return '⏰';
      case 'Work From Home': return '🏠';
      case 'On Duty': return '🚗';
      default: return '📝';
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="regularization-approval-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Regularization Requests</h1>
          <p className="text-gray-500">Approve or reject attendance correction requests</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-yellow-50 border-yellow-200 cursor-pointer hover:shadow-md"
              onClick={() => setFilter('Pending')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
                <p className="text-sm text-yellow-600">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200 cursor-pointer hover:shadow-md"
              onClick={() => setFilter('Approved')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
                <p className="text-sm text-green-600">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200 cursor-pointer hover:shadow-md"
              onClick={() => setFilter('Rejected')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <XCircle className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
                <p className="text-sm text-red-600">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md"
              onClick={() => setFilter('All')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-gray-600" />
              <div>
                <p className="text-2xl font-bold text-gray-700">{stats.pending + stats.approved + stats.rejected}</p>
                <p className="text-sm text-gray-600">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Badge */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-gray-500" />
        <span className="text-sm text-gray-500">Showing:</span>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(filter === 'All' ? '' : filter)}`}>
          {filter}
        </span>
      </div>

      {/* Requests List */}
      <Card>
        <CardHeader>
          <CardTitle>Regularization Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No {filter.toLowerCase()} regularization requests</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {requests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{request.staff_name}</p>
                          <p className="text-sm text-gray-500">{request.employee_id}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatDate(request.date)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1">
                          <span>{getRequestTypeIcon(request.request_type)}</span>
                          <span className="text-sm">{request.request_type}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {request.reason || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {request.status === 'Pending' ? (
                          <Button size="sm" onClick={() => openRequest(request)} data-testid={`review-btn-${request.id}`}>
                            Review
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => openRequest(request)}>
                            View
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-bold">
                {selectedRequest.status === 'Pending' ? 'Review Request' : 'Request Details'}
              </h3>
              <button onClick={() => { setSelectedRequest(null); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <User className="h-10 w-10 text-gray-400 bg-white rounded-full p-2" />
                  <div>
                    <p className="font-semibold">{selectedRequest.staff_name}</p>
                    <p className="text-sm text-gray-500">{selectedRequest.employee_id} • {selectedRequest.department}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <p className="text-xs text-gray-500">Date</p>
                    <p className="font-medium">{formatDate(selectedRequest.date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Request Type</p>
                    <p className="font-medium">{getRequestTypeIcon(selectedRequest.request_type)} {selectedRequest.request_type}</p>
                  </div>
                  {selectedRequest.requested_check_in && (
                    <div>
                      <p className="text-xs text-gray-500">Requested Check-In</p>
                      <p className="font-medium">{selectedRequest.requested_check_in}</p>
                    </div>
                  )}
                  {selectedRequest.requested_check_out && (
                    <div>
                      <p className="text-xs text-gray-500">Requested Check-Out</p>
                      <p className="font-medium">{selectedRequest.requested_check_out}</p>
                    </div>
                  )}
                </div>

                {selectedRequest.reason && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500">Reason</p>
                    <p className="text-sm bg-white p-2 rounded mt-1">{selectedRequest.reason}</p>
                  </div>
                )}

                {selectedRequest.admin_remarks && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500">Admin Remarks</p>
                    <p className="text-sm bg-white p-2 rounded mt-1">{selectedRequest.admin_remarks}</p>
                  </div>
                )}
              </div>

              {selectedRequest.status === 'Pending' && (
                <>
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Approval Details</h4>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Approved Status
                        </label>
                        <select
                          value={formData.approved_status}
                          onChange={(e) => setFormData({...formData, approved_status: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                        >
                          <option value="Present">Present</option>
                          <option value="Half Day">Half Day</option>
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Check-In</label>
                          <input
                            type="time"
                            value={formData.approved_check_in}
                            onChange={(e) => setFormData({...formData, approved_check_in: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Check-Out</label>
                          <input
                            type="time"
                            value={formData.approved_check_out}
                            onChange={(e) => setFormData({...formData, approved_check_out: e.target.value})}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Remarks (Required for rejection)
                        </label>
                        <textarea
                          value={formData.remarks}
                          onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg"
                          rows={2}
                          placeholder="Add remarks..."
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setSelectedRequest(null); resetForm(); }}
                  className="flex-1"
                >
                  Close
                </Button>
                
                {selectedRequest.status === 'Pending' && (
                  <>
                    <Button
                      onClick={handleReject}
                      disabled={processing}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      data-testid="reject-btn"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      onClick={handleApprove}
                      disabled={processing}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid="approve-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
